import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { PeriodType } from '../../../utils/orgBillingAnalysisUtils';

const MONTH_OPTIONS = [
  { value: '1', label: 'Jan' },
  { value: '2', label: 'Feb' },
  { value: '3', label: 'Mar' },
  { value: '4', label: 'Apr' },
  { value: '5', label: 'May' },
  { value: '6', label: 'Jun' },
  { value: '7', label: 'Jul' },
  { value: '8', label: 'Aug' },
  { value: '9', label: 'Sep' },
  { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dec' },
];

const FY_QUARTERS = [
  { value: 'all', label: 'All' },
  { value: 'Q1', label: 'Q1' },
  { value: 'Q2', label: 'Q2' },
  { value: 'Q3', label: 'Q3' },
  { value: 'Q4', label: 'Q4' },
];

const FilterSelect: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}> = ({ label, value, onChange, options }) => (
  <div className="flex items-center gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 shrink-0">
      {label}
    </span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer max-w-[9.5rem] truncate"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);

const PeriodToggle: React.FC<{
  value: PeriodType;
  onChange: (v: PeriodType) => void;
}> = ({ value, onChange }) => (
  <div className="flex items-center bg-slate-100 rounded-lg p-0.5 gap-0.5">
    {(
      [
        ['fy_india', 'FY India'],
        ['calendar', 'Calendar'],
      ] as [PeriodType, string][]
    ).map(([pt, lbl]) => (
      <button
        key={pt}
        type="button"
        onClick={() => onChange(pt)}
        className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
          value === pt
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-slate-600 hover:text-slate-800'
        }`}
      >
        {lbl}
      </button>
    ))}
  </div>
);

export interface TechnologyBillingDrillScopeFiltersProps {
  filterLabel: string;
  selectedTechnologies: string[];
  onSelectedTechnologiesChange: (next: string[]) => void;
  technologyOptions: string[];
  /** Long lists (e.g. projects): show a search box in the dropdown. */
  enableOptionSearch?: boolean;
  searchPlaceholder?: string;
  /** When nothing is selected, button label (default: All technologies). */
  emptySelectionSummary?: string;
}

/** Multi-select slice filter for the drill-down card (scoped to table + export). */
export function TechnologyBillingDrillScopeFilters({
  filterLabel,
  selectedTechnologies,
  onSelectedTechnologiesChange,
  technologyOptions,
  enableOptionSearch = false,
  searchPlaceholder = 'Search…',
  emptySelectionSummary = 'All technologies',
}: TechnologyBillingDrillScopeFiltersProps) {
  const [open, setOpen] = useState(false);
  const [optionQuery, setOptionQuery] = useState('');
  const [draftSelected, setDraftSelected] = useState<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setOptionQuery('');
  }, [open]);

  useEffect(() => {
    if (open) setDraftSelected([...selectedTechnologies]);
  }, [open, selectedTechnologies]);

  const filteredOptions = useMemo(() => {
    const q = optionQuery.trim().toLowerCase();
    if (!q) return technologyOptions;
    return technologyOptions.filter((t) => t.toLowerCase().includes(q));
  }, [technologyOptions, optionQuery]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selectionEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((v, i) => v === sb[i]);
  };

  const toggleDraft = (t: string) => {
    setDraftSelected((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  };

  const handleApply = () => {
    onSelectedTechnologiesChange(draftSelected);
    setOpen(false);
  };

  const canApply = !selectionEqual(draftSelected, selectedTechnologies);

  const summary =
    selectedTechnologies.length === 0
      ? emptySelectionSummary
      : selectedTechnologies.length === 1
        ? selectedTechnologies[0]
        : `${selectedTechnologies.length} selected`;

  return (
    <div ref={rootRef} className="relative flex items-center gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 shrink-0">
        {filterLabel}
      </span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex items-center gap-1 min-w-[7.5rem] max-w-[14rem] text-left text-[12px] font-medium text-slate-700 bg-slate-100 border-0 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className="truncate flex-1">{summary}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div
          className={`absolute right-0 top-full z-40 mt-1 max-h-[22rem] overflow-hidden flex flex-col rounded-lg border border-slate-200 bg-white shadow-lg ${
            enableOptionSearch ? 'w-72' : 'w-56'
          }`}
          role="listbox"
          aria-multiselectable
        >
          {enableOptionSearch ? (
            <div className="px-2 pt-2 pb-1 border-b border-slate-100 shrink-0">
              <input
                type="search"
                value={optionQuery}
                onChange={(e) => setOptionQuery(e.target.value)}
                placeholder={searchPlaceholder}
                autoComplete="off"
                className="w-full text-[12px] text-slate-800 placeholder:text-slate-400 border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ) : null}
          <div className="overflow-y-auto flex-1 min-h-0 py-1 max-h-48">
            {filteredOptions.length === 0 ? (
              <p className="px-2 py-2 text-[11px] text-slate-500">
                No matches
              </p>
            ) : (
              filteredOptions.map((t) => {
                const checked = draftSelected.includes(t);
                return (
                  <label
                    key={t}
                    className="flex items-center gap-2 px-2 py-1 text-[12px] text-slate-700 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleDraft(t)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="truncate" title={t}>
                      {t}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between gap-2 px-2 py-2 border-t border-slate-100 bg-slate-50/90 shrink-0">
            <button
              type="button"
              className="text-[10px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-40 px-1 py-0.5"
              disabled={draftSelected.length === 0}
              onClick={() => setDraftSelected([])}
            >
              Clear
            </button>
            <button
              type="button"
              className="text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-md px-2.5 py-1.5 shadow-sm"
              disabled={!canApply}
              onClick={handleApply}
            >
              Apply filter
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export interface TechnologyBillingFiltersProps {
  periodType: PeriodType;
  onPeriodType: (v: PeriodType) => void;
  fyYear: string;
  onFyYear: (v: string) => void;
  fyYearOptions: number[];
  fyQuarter: string;
  onFyQuarter: (v: string) => void;
  tsYear: string;
  onTsYear: (v: string) => void;
  calendarYearOptions: number[];
  tsMonth: string;
  onTsMonth: (v: string) => void;
  onReset: () => void;
}

/** Toolbar aligned with Account Owner Portfolio / Regional header filters */
export function TechnologyBillingFilters({
  periodType,
  onPeriodType,
  fyYear,
  onFyYear,
  fyYearOptions,
  fyQuarter,
  onFyQuarter,
  tsYear,
  onTsYear,
  calendarYearOptions,
  tsMonth,
  onTsMonth,
  onReset,
}: TechnologyBillingFiltersProps) {
  const fySelectOptions = [
    { value: 'all', label: 'All FY' },
    ...fyYearOptions.map((y) => ({
      value: String(y),
      label: `FY ${y}–${String(y + 1).slice(-2)}`,
    })),
  ];

  const yearOpts = [
    { value: 'all', label: 'All years' },
    ...calendarYearOptions.map((y) => ({ value: String(y), label: String(y) })),
  ];

  return (
    <div className="flex items-center gap-2 flex-wrap justify-end">
      <PeriodToggle value={periodType} onChange={onPeriodType} />
      <div className="h-4 w-px bg-slate-200 flex-none" />

      {periodType === 'fy_india' ? (
        <>
          <FilterSelect
            label="FY"
            value={fyYear}
            onChange={onFyYear}
            options={fySelectOptions}
          />
          <div className="h-4 w-px bg-slate-200 flex-none" />
          <FilterSelect
            label="Q"
            value={fyQuarter}
            onChange={onFyQuarter}
            options={FY_QUARTERS}
          />
        </>
      ) : (
        <>
          <FilterSelect
            label="Year"
            value={tsYear}
            onChange={onTsYear}
            options={yearOpts}
          />
          <div className="h-4 w-px bg-slate-200 flex-none" />
          <FilterSelect
            label="Mo"
            value={tsMonth}
            onChange={onTsMonth}
            options={[{ value: 'all', label: 'All' }, ...MONTH_OPTIONS]}
          />
        </>
      )}

      <div className="h-4 w-px bg-slate-200 flex-none" />
      <button
        type="button"
        onClick={onReset}
        className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 px-2 py-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
      >
        Reset
      </button>
    </div>
  );
}
